import React, { useState, useEffect } from 'react';
import { 
  BoltIcon, 
  WrenchScrewdriverIcon,
  PauseIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import KPICard from '../../components/Cards/KPICard';
import EnergyForecastChart from '../../components/charts/EnergyForecastChart';
import EquipmentPieChart from '../../components/charts/EquipmentPieChart';
import { monitoringApi, DashboardOverview, PowerTrend } from '../../services/monitoringApi';


interface DashboardData {
  overview: DashboardOverview | null;
  powerTrends: PowerTrend[];
  equipmentContribution: Array<{
    name: string;
    value: number;
    color: string;
  }>;
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [timeRange, setTimeRange] = useState('24h');
  const [chartType, setChartType] = useState<'line' | 'area'>('area');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Use mock data for visualization
      const mockOverview = {
        total_power: 2847.5,
        equipment_status: {
          total_active: 24,
          inverters: 12,
          meters: 8,
          weather_stations: 4
        },
        alerts: {
          critical: 2,
          warning: 5,
          info: 8
        }
      };

      const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
      const mockPowerTrends = Array.from({ length: Math.min(hours, 24) }, (_, i) => ({
        hour: new Date(Date.now() - (23 - i) * 60 * 60 * 1000).toISOString(),
        avg_power: Math.sin(i * Math.PI / 12) * 1000 + 1500 + Math.random() * 200,
        max_power: Math.sin(i * Math.PI / 12) * 1200 + 1800 + Math.random() * 150
      }));

      const mockEquipmentContribution = [
        { name: 'Inverters', value: 1847, color: '#10B981' },
        { name: 'Meters', value: 650, color: '#3B82F6' },
        { name: 'Weather Stations', value: 350, color: '#8B5CF6' }
      ];
      
      setData({
        overview: mockOverview,
        powerTrends: mockPowerTrends,
        equipmentContribution: mockEquipmentContribution
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold gradient-neural">Performance Dashboard</h1>
          <p className="text-slate-400 mt-2">High-level analytics for engineers and supervisors</p>
        </div>
        
        <div className="flex items-center gap-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="ai-input text-sm py-2 px-3"
          >
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>
        </div>
      </div>

      {/* Equipment Status KPIs */}
      <div className="grid-1-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <BoltIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data?.overview?.equipment_status.total_active || 0}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">units</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Active Systems</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Online & Operational</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl flex items-center justify-center">
              <PauseIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data?.overview?.total_power.toFixed(1) || '0.0'}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">kW</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Current Power</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Live Generation</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-pink-600 rounded-xl flex items-center justify-center">
              <WrenchScrewdriverIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {data?.overview?.equipment_status?.inverters || 0}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">inverters</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Inverters</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">Power Generation</div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-white" />
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {(data?.overview?.alerts.critical || 0) + (data?.overview?.alerts.warning || 0) + (data?.overview?.alerts.info || 0)}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">alerts</div>
            </div>
          </div>
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900 dark:text-white">Total Alerts</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{data?.overview?.alerts.critical || 0} Critical</div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="chart-grid">
        {/* Energy vs Forecast Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h2 className="chart-title">Energy vs Forecast</h2>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setChartType('line')}
                className={`ai-btn-ghost text-sm ${chartType === 'line' ? 'text-violet-400' : ''}`}
              >
                Line
              </button>
              <button
                onClick={() => setChartType('area')}
                className={`ai-btn-ghost text-sm ${chartType === 'area' ? 'text-violet-400' : ''}`}
              >
                Area
              </button>
            </div>
          </div>
          
          <div className="relative">
            <EnergyForecastChart 
              data={data?.powerTrends?.map(trend => ({
                time: new Date(trend.hour).getHours() + ':00',
                actual: trend.avg_power,
                forecast: trend.max_power
              })) || []} 
              chartType={chartType}
              loading={false} 
            />
          </div>
        </div>

        {/* Equipment Contribution Pie Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h2 className="chart-title">Equipment Contribution</h2>
          </div>
          
          <div className="relative">
            <EquipmentPieChart 
              data={data?.equipmentContribution || []} 
              loading={false} 
            />
          </div>
        </div>
      </div>

      {/* Alerts Summary */}
      <div className="chart-container mt-8">
        <div className="chart-header">
          <h2 className="chart-title">Alerts Summary</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative z-10">
          <div className="flex items-center justify-between p-4 rounded-xl bg-red-500/10 border border-red-500/20">
            <div>
              <div className="text-2xl font-bold text-red-400">{data?.overview?.alerts?.critical || 0}</div>
              <div className="text-sm text-red-300">Critical Alerts</div>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-red-400" />
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
            <div>
              <div className="text-2xl font-bold text-yellow-400">{data?.overview?.alerts?.warning || 0}</div>
              <div className="text-sm text-yellow-300">Warning Alerts</div>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-yellow-400" />
          </div>
          
          <div className="flex items-center justify-between p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
            <div>
              <div className="text-2xl font-bold text-blue-400">{data?.overview?.alerts?.info || 0}</div>
              <div className="text-sm text-blue-300">Info Alerts</div>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-blue-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
